// Thin wrapper around epub.js. We avoid pulling epub.js into the import path
// of bookStorageService directly, so unit tests / non-reader code paths stay
// lighter.

import ePub, { type Book as EpubBook, type Rendition } from "epubjs";

export interface EpubMetadataResult {
  title?: string;
  author?: string;
  coverBlob?: Blob;
  /** dc:language from the OPF, e.g. "en", "de", "ja". */
  language?: string;
  /** Spine href of the first section that actually contains prose.
   *  Computed once at import — see findFirstContentHref(). Undefined when
   *  nothing qualified, in which case the reader opens as it always did. */
  firstContentHref?: string;
  /** Real text size of the book — drives the time-remaining estimate. */
  wordCount?: number;
  charCount?: number;
}

/** A section needs at least this much visible text to count as content
 *  rather than a cover / title / half-title plate. 250 sits above the
 *  longest wordless front-matter page measured across the live 19-book
 *  library (161 chars) and below the shortest real one (340). */
const MIN_CONTENT_CHARS = 250;

/** Only look this far into the spine. Front matter that runs past 20
 *  sections isn't front matter, and the cap bounds the work on a
 *  100+ section book. */
const MAX_SECTIONS_TO_PROBE = 20;

/** Strip markup and entities, measure what's left. Deliberately crude: the
 *  job is telling an image-only plate from a page of prose, which does not
 *  need a parser. */
function visibleTextLength(src: string): number {
  return src
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&[a-zA-Z#0-9]+;/g, " ")
    .replace(/\s+/g, " ")
    .trim().length;
}

/** Walk the spine and return the href of the first section carrying real
 *  prose, so a cover-first book can be opened on actual text.
 *
 *  WHY THIS READS THE ARCHIVE INSTEAD OF CALLING section.load(): the
 *  abandoned experiment recorded in ReaderView.tsx did this scan at OPEN
 *  time against a live rendition. section.load() disturbed the rendition
 *  epub.js was mid-way through building and took the library from 17/19
 *  opening to 14/19. book.archive.getText() pulls the raw zip entry with no
 *  document construction, no section state and no rendition involvement —
 *  and here it runs at IMPORT time, when no rendition exists at all.
 *
 *  Measured over all 19 books in the live library: finds a section for
 *  19/19, median 2 ms, worst case 58 ms (the 55 MB Future Boy).
 *
 *  This finds the first section with *text*, which for several books is the
 *  copyright page rather than chapter one. That is intended and sufficient:
 *  the goal is that the reader never lands on a wordless plate and
 *  Read-aloud always has words. The EPUBs' own `bodymatter` landmarks were
 *  evaluated as a better signal and rejected — only 5 of the 19 books carry
 *  one, and 4 of those 5 point at a wordless page. */
export async function findFirstContentHref(
  book: EpubBook,
): Promise<string | undefined> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyBook = book as any;
    const items: Array<{ href?: string; url?: string; linear?: boolean }> =
      anyBook.spine?.spineItems ?? [];
    const archive = anyBook.archive;
    if (!items.length || !archive?.getText) return undefined;

    const limit = Math.min(items.length, MAX_SECTIONS_TO_PROBE);
    for (let i = 0; i < limit; i++) {
      const item = items[i];
      // linear="no" is "supplementary" by definition — never a sensible
      // place to open a book.
      if (item.linear === false) continue;
      if (!item.url || !item.href) continue;
      let src: string | undefined;
      try {
        src = await archive.getText(item.url);
      } catch {
        continue; // unreadable entry — try the next section
      }
      if (!src) continue;
      if (visibleTextLength(src) >= MIN_CONTENT_CHARS) return item.href;
    }
  } catch (err) {
    // Never let this block an import. No href just means the reader opens
    // the book exactly the way it did before.
    console.warn("[pagevoice] first-content scan failed", err);
  }
  return undefined;
}

/** Total words and characters of readable text in the whole book.
 *
 *  2026-09-29: the reading-time footer used to fall back to a hardcoded
 *  80,000 words whenever it had no real figure — and it ALWAYS had no real
 *  figure, because the state that was meant to hold the count was never
 *  assigned. Every book therefore showed 80000/250wpm = 320 min, i.e. the
 *  identical "5 hrs 20 mins left" seen on 13 of 19 books, on a 919 KB
 *  Don Quijote and a 41 KB art catalogue alike.
 *
 *  Counted here, once, at import — same place and same mechanism as
 *  findFirstContentHref, reading raw zip entries rather than loading
 *  sections, so it never touches a rendition.
 *
 *  Returns undefined rather than a guess if nothing could be read: the
 *  footer renders nothing at all in that case, which is honest. */
export async function countBookText(
  book: EpubBook,
): Promise<{ words: number; chars: number } | undefined> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyBook = book as any;
    const items: Array<{ url?: string; linear?: boolean }> =
      anyBook.spine?.spineItems ?? [];
    const archive = anyBook.archive;
    if (!items.length || !archive?.getText) return undefined;

    let chars = 0;
    let words = 0;
    let read = 0;
    for (const item of items) {
      if (!item.url) continue;
      let src: string | undefined;
      try {
        src = await archive.getText(item.url);
      } catch {
        continue;
      }
      if (!src) continue;
      const text = src
        .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, " ")
        .replace(/<[^>]+>/g, " ")
        .replace(/&[a-zA-Z#0-9]+;/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      if (!text) continue;
      chars += text.length;
      // Cheap and close enough: split on whitespace. We're feeding a
      // minutes-level estimate, not a word processor.
      words += text.split(" ").length;
      read++;
    }
    if (!read || !words) return undefined;
    return { words, chars };
  } catch (err) {
    console.warn("[pagevoice] word count failed", err);
    return undefined;
  }
}

/** Open an EPUB blob just long enough to extract metadata + cover. */
export async function extractEpubMetadata(
  file: Blob
): Promise<EpubMetadataResult> {
  let book: EpubBook | undefined;
  try {
    // 2026-10-07 — this function still materialises the whole file. The
    // identical allocation was removed from sha256Hex this morning and the
    // right fix here is the same (hand epub.js a blob: URL instead), but it
    // is NOT going into the build that fixes covers: this is the function
    // that PRODUCES covers, and my verification of the change did not
    // finish. Shipping an unverified change to the cover path inside the
    // cover fix is how a fix becomes a regression. Taken separately, next.
    const buffer = await file.arrayBuffer();
    book = ePub(buffer);
    await book.ready;
    const meta = await book.loaded.metadata;
    let coverBlob: Blob | undefined;
    try {
      const coverUrl = await book.coverUrl();
      if (coverUrl) {
        // coverUrl is a blob: URL produced by epub.js inside our origin —
        // fetching it back into a Blob is cheap.
        const resp = await fetch(coverUrl);
        if (resp.ok) coverBlob = await resp.blob();
      }
    } catch {
      // Cover extraction is best-effort.
    }
    // Cheap because the spine is already parsed and the archive already
    // open — this adds a handful of zip reads, not a second pass over the
    // file. Best-effort: findFirstContentHref swallows its own errors.
    const firstContentHref = await findFirstContentHref(book);
    const counts = await countBookText(book);

    return {
      title: meta?.title,
      author: meta?.creator,
      coverBlob,
      language: meta?.language,
      firstContentHref,
      wordCount: counts?.words,
      charCount: counts?.chars,
    };
  } catch (err) {
    // Some malformed EPUBs throw; we surface the original filename then.
    console.warn("[pagevoice] epub metadata extraction failed", err);
    return {};
  } finally {
    try {
      book?.destroy();
    } catch {
      /* noop */
    }
  }
}

/** Open an EPUB blob and return a fresh epub.js Book ready for rendering. */
export async function openEpub(blob: Blob): Promise<EpubBook> {
  const buffer = await blob.arrayBuffer();
  const book = ePub(buffer);
  await book.ready;
  return book;
}

/** Helper to apply our theme/font/margin to an active rendition. */
export function applyReaderTheme(
  rendition: Rendition,
  opts: {
    theme: "paper" | "white" | "sepia" | "dark" | "gray" | "eink-light" | "eink-dark";
    fontSize: number;
    fontFamily: string;
    lineHeight: number;
    margin: number;
  }
) {
  const palettes: Record<string, { bg: string; fg: string; link: string }> = {
    // Warm off-white "paper/day" theme (Apple Books look) — the default.
    paper: { bg: "#fdfcf8", fg: "#1b1712", link: "#5a4a2a" },
    white: { bg: "#ffffff", fg: "#1a1a1a", link: "#3b3b3b" },
    sepia: { bg: "#f4ecd8", fg: "#5b4636", link: "#704214" },
    gray: { bg: "#cfd2cd", fg: "#1a1a1a", link: "#222" },
    // House dark palette (1n2.org). This is now the default reading surface.
    dark: { bg: "#0a0a0a", fg: "#d8d8d8", link: "#f7931a" },
    "eink-light": { bg: "#ffffff", fg: "#000000", link: "#000000" },
    "eink-dark": { bg: "#000000", fg: "#ffffff", link: "#ffffff" },
  };
  const p = palettes[opts.theme] ?? palettes.paper;
  const m = `${opts.margin}px`;
  // The paper/day theme reads best justified with hyphenation (mirrors the
  // reference). Every other theme stays left-aligned to avoid "word rivers"
  // on a narrow column.
  const align = opts.theme === "paper" ? "justify" : "left";
  // Warm peach selection/annotation tint — the highlight colour of the new
  // theme. Kept consistent across themes so a highlight looks the same.
  const peachSelection = "rgba(249, 217, 168, 0.75)";

  const themeStyles: Record<string, Record<string, string>> = {
    body: {
      background: `${p.bg} !important`,
      color: `${p.fg} !important`,
      "font-family": `${opts.fontFamily} !important`,
      "font-size": `${opts.fontSize}px !important`,
      "line-height": `${opts.lineHeight} !important`,
      "padding-left": `${m} !important`,
      "padding-right": `${m} !important`,
      // Publisher CSS often ships text-align: justify; on non-paper themes we
      // force left to avoid "word rivers" on a narrow column. The paper/day
      // theme intentionally justifies WITH hyphenation to mirror the
      // reference (Apple Books) look — hyphens keep the gaps tight.
      "text-align": `${align} !important`,
      "word-spacing": "normal !important",
      hyphens: "auto",
      "-webkit-hyphens": "auto",
    },
    p: {
      "font-family": `${opts.fontFamily} !important`,
      "font-size": `${opts.fontSize}px !important`,
      "line-height": `${opts.lineHeight} !important`,
      color: `${p.fg} !important`,
      "text-align": `${align} !important`,
      "word-spacing": "normal !important",
      hyphens: "auto",
      "-webkit-hyphens": "auto",
    },
    h1: { color: `${p.fg} !important` },
    h2: { color: `${p.fg} !important` },
    h3: { color: `${p.fg} !important` },
    a: {
      color: `${p.link} !important`,
    },
    "::selection": {
      background: peachSelection,
    },
    // Soft paragraph-level tint while TTS is in this paragraph. Kept
    // faint so the per-word highlight is the eye-tracking signal. Until
    // the per-word CSS below was added to this same iframe-side
    // injection (260626-0700), this rule was the ONLY highlight that
    // reached inside the rendered iframe — which is why the paragraph
    // tint dominated and looked like the whole page was lit up.
    ".pv-tts-active": {
      "background-color": "rgba(255, 230, 100, 0.08) !important",
      "border-radius": "2px",
      transition: "background-color 120ms ease-out",
    },
    // Per-word highlight. wrapWords() injects .pv-w spans around each
    // token in the active paragraph; .pv-w-active moves from word to
    // word as TTS speech advances (real onBoundary events from browser
    // voices, or the time-based fallback for Piper and iOS voices that
    // don't fire boundaries). box-decoration-break:clone makes the
    // highlight render per-line if a word ever wraps; cheap insurance.
    // 2026-10-06 — font-weight REMOVED. This is the copy that actually
    // reaches the rendered text (it is injected into the epub.js iframe;
    // the rule in index.css applies to the outer document). It carried
    // `font-weight: 600 !important`, which widens the spoken word, re-wraps
    // its line and shifts every line below it — once per word. Reported by
    // Thomas mid-read as "changing the size of the text to highlight it and
    // moving it around".
    //
    // A HIGHLIGHT MAY ONLY CHANGE COLOURS. Background, foreground and
    // box-shadow paint outside layout; anything that touches font metrics
    // or box geometry does not belong here. The sibling rule in index.css
    // carries the same note.
    ".pv-w-active": {
      "background-color": "rgba(255, 200, 50, 0.95) !important",
      color: "#1a1a1a !important",
      "border-radius": "3px",
      "box-shadow": "0 0 0 2px rgba(255, 200, 50, 0.95)",
      "box-decoration-break": "clone",
      "-webkit-box-decoration-break": "clone",
    },
  };

  // epub.js theme API accepts a record of selector -> style record at runtime
  // and merges them into the default theme. The cast keeps TS quiet because
  // upstream types are stricter than the runtime shape.
  type ThemesRuntime = {
    default: (styles: Record<string, Record<string, string>>) => void;
    fontSize?: (size: string) => void;
  };
  const themes = rendition.themes as unknown as ThemesRuntime;
  themes.default(themeStyles);
  // Belt-and-suspenders: epub.js exposes a direct fontSize setter that some
  // builds prefer over the CSS injection above.
  themes.fontSize?.(`${opts.fontSize}px`);
}
